/**
 * The camera: a full-screen live decorrelation stretch, and a store of shots.
 *
 * # It is an app, not a page
 *
 * The camera view fills the viewport. There is no site header and no
 * fullscreen button, because there is nothing to go fullscreen *from* — the
 * view is already the whole screen, and the browser's own Fullscreen API is
 * refused on an iPhone in any case. The controls are three overlays: a red
 * shutter, a cogwheel for settings, and the last shot in the lower-left corner,
 * which opens the shots panel. Settings carries the one link out, to the
 * gallery page. Picking "Adjust" on a stored shot opens a fourth overlay — the
 * adjust panel — which mirrors the settings' chip row and gain slider but keeps
 * its own state, and shows the computed image as it changes.
 *
 * # A capture is stored, not just displayed
 *
 * The shutter writes the frame to IndexedDB as two images: the untouched
 * original, and the stretch. The original is what makes a shot re-editable —
 * picking a different preset later re-runs the stretch from the source pixels
 * instead of stretching an already-stretched image.
 *
 * # The heavy work is off the main thread
 *
 * A full-resolution still is a full-resolution stretch. On a 4K frame, LAB
 * walks 8.3 million pixels through a 32³ table, which is seconds of work.
 * Sending the frame to `shot-worker.ts` keeps the live view live while that
 * runs, and lets several captures overlap.
 *
 * # The live path keeps its three throttles
 *
 * The frame never touches the CPU — `drawElement` uploads the `<video>` into a
 * texture. Statistics run at 1 Hz on a 160x120 capture and are smoothed with an
 * exponential filter, so the output level does not step when one frame is
 * brighter than the last. The preview draws at a fraction of the stream's size,
 * chosen by the resolution slider; only the shutter pays for full resolution.
 *
 * # Presets on video
 *
 * An **adaptive** matrix is recomputed from the image's own covariance, so on
 * video the picture boils. A **fixed** preset keeps `M` constant and lets only
 * the output level follow the mean. The live default is therefore fixed (YDS),
 * and the one adaptive preset (Auto) says so on its chip blurb.
 *
 * @module web/js/camera
 */

import type { PresetNeeds, Stats } from '../../core/types.ts';
import { allPresetSummaries, LAB_PRESET, presetById } from '../../core/presets.ts';
import { collapse } from '../../core/compose.ts';
import { GlRenderer, quantise, type GlAffine } from './gl.ts';
import { labLutCached, rasterFromElement, rasterStats } from './cpu.ts';
import { el, header, provenanceTag } from './dom.ts';
import { APP_SLUG, titleFor } from './brand.ts';
import { addShot, deleteShot, listShots, putShot, type Shot } from './shot-store.ts';
import { zipBlob } from './zip.ts';
import type { DoneReply, UpdatedReply, WorkerReply, WorkerRequest } from './shot-worker.ts';

/** How often the statistics are re-measured, in milliseconds. */
const STATS_INTERVAL_MS = 1000;
/** Weight of a new measurement in the exponential filter. */
const SMOOTH_ALPHA = 0.25;
/** Size of the capture the statistics are measured from. */
const STATS_W = 160;
const STATS_H = 120;
/** How often the readout line is rewritten, in milliseconds. */
const READOUT_INTERVAL_MS = 250;
/** How long to wait after the last settings change before re-rendering a shot. */
const RERENDER_DEBOUNCE_MS = 250;

/** The live default: fixed, so the picture does not flicker. */
const DEFAULT_PRESET = 'yds';
/** Slider range for the band-gain multiplier. */
const GAIN_MIN = 0.25;
const GAIN_MAX = 3;

/**
 * Live-preview widths, as proportions of the stream's native width.
 *
 * Proportions rather than fixed pixel counts, so the slider means the same
 * thing on a 720p webcam and a 4K phone camera.
 */
const LIVE_SCALES = [0.25, 0.35, 0.5, 0.7, 1] as const;
/** The live width the slider starts on, when the native width is larger. */
const DEFAULT_LIVE_WIDTH = 640;
/** Never select a live width below this, unless native is already smaller. */
const MIN_LIVE_WIDTH = 160;

/** Every preset, linear and nonlinear, in the order the chips show them. */
const SUMMARIES = allPresetSummaries();

/** The name, blurb and requirements of a preset, LAB included. */
interface PresetMeta {
  id: string;
  name: string;
  blurb: string;
  provenance: string;
  fixed: boolean;
  needs: PresetNeeds;
}

/**
 * `presetById` does not know LAB, because LAB is nonlinear and has no `build`.
 * This fills that gap, the same way `build/build.ts` and `web/js/upload.ts` do.
 */
function metaFor(id: string): PresetMeta {
  const summary = SUMMARIES.find((s) => s.id === id);
  if (id === LAB_PRESET.id) {
    return {
      id,
      name: LAB_PRESET.name,
      blurb: LAB_PRESET.blurb,
      provenance: LAB_PRESET.provenance,
      fixed: true,
      needs: 'none',
    };
  }
  const preset = presetById(id);
  return {
    id,
    name: preset.name,
    blurb: summary?.blurb ?? preset.blurb,
    provenance: preset.provenance,
    fixed: preset.fixed === true,
    needs: preset.needs ?? 'covariance',
  };
}

/** True when the preset ignores the gain, because it has no bands to scale. */
function gainIgnored(id: string): boolean {
  return metaFor(id).needs === 'none';
}

/**
 * A preset chip row and the gain slider that belongs with it.
 *
 * The live view and the shot editor each need their own copy. Sharing one set
 * would make a change to a stored shot reconfigure the camera, which is the
 * state-mixing the separate panels exist to avoid.
 */
interface PresetControls {
  /** The chips, the blurb, the provenance tag and the gain slider, in order. */
  root: HTMLElement;
  /** Redraw the whole group for `preset` and `gain`. */
  sync(preset: string, gain: number): void;
}

function presetControls(
  onPreset: (id: string) => void,
  onGain: (gain: number) => void,
  gainAria = 'Gain',
): PresetControls {
  const chips = el('div', { class: 'chips', role: 'tablist', attrs: { 'aria-label': 'Preset' } });
  for (const summary of SUMMARIES) {
    chips.append(
      el('button', {
        class: 'chip',
        type: 'button',
        text: summary.name,
        dataset: { preset: summary.id },
        attrs: { 'aria-pressed': 'false' },
        on: { click: () => onPreset(summary.id) },
      }),
    );
  }

  const blurb = el('p', { class: 'note' });
  const provenance = el('span', { class: 'cam__provenance' });
  const gainValue = el('span', { class: 'note cam__gain-value', text: 'x1.00' });
  const gain = el('input', {
    type: 'range',
    attrs: {
      min: String(GAIN_MIN),
      max: String(GAIN_MAX),
      step: '0.05',
      value: '1',
      'aria-label': gainAria,
    },
  });
  gain.addEventListener('input', () => onGain(clampGain(Number(gain.value))));
  const gainRow = el('div', { class: 'cam__field' }, [gain, gainValue]);
  const root = el('div', {}, [
    chips,
    blurb,
    provenance,
    el('label', { class: 'field', text: 'Gain' }, []),
    gainRow,
  ]);

  const sync = (preset: string, gainNow: number): void => {
    const meta = metaFor(preset);
    for (const btn of chips.querySelectorAll('button')) {
      btn.setAttribute('aria-pressed', String(btn.dataset.preset === preset));
    }
    const ignored = gainIgnored(preset);
    gain.disabled = ignored;
    gainRow.classList.toggle('is-off', ignored);
    gain.value = String(gainNow);
    gainValue.textContent = ignored ? 'fixed' : `x${gainNow.toFixed(2)}`;
    blurb.replaceChildren(
      el('span', { text: meta.blurb }),
      ' ',
      el('strong', {
        text: ignored
          ? 'Nonlinear: a 32³ table, one lookup per pixel.'
          : meta.fixed
            ? 'Fixed matrix: steady on video.'
            : 'Adaptive matrix: the picture will flicker.',
      }),
    );
    provenance.replaceChildren(provenanceTag(meta.provenance));
  };

  return { root, sync };
}

/** A `<video>` that may or may not have the frame-callback method. */
type VideoWithCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, metadata: unknown) => void) => number;
};

/**
 * Call `cb` once per presented video frame, falling back to the animation
 * frame. `requestVideoFrameCallback` is the better clock: it fires once per
 * *decoded* frame, so a 24 fps stream is not stretched to 60 redraws a second.
 */
function nextFrame(video: HTMLVideoElement, cb: () => void): void {
  const v = video as VideoWithCallback;
  if (typeof v.requestVideoFrameCallback === 'function') {
    v.requestVideoFrameCallback(() => cb());
  } else {
    requestAnimationFrame(() => cb());
  }
}

/** The GPU's own name for itself, for the readout. */
function rendererName(gl: WebGL2RenderingContext): string {
  const ext = gl.getExtension('WEBGL_debug_renderer_info');
  if (ext) return String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL));
  return String(gl.getParameter(gl.RENDERER));
}

/**
 * Exponential filter over two `Stats`.
 *
 * The covariance is filtered entrywise. It is a symmetric matrix and both sides
 * of the average are symmetric, so the result stays symmetric.
 */
function blend(prev: Stats, next: Stats): Stats {
  const a = SMOOTH_ALPHA;
  const b = 1 - a;
  const mean = Float64Array.of(
    a * next.mean[0]! + b * prev.mean[0]!,
    a * next.mean[1]! + b * prev.mean[1]!,
    a * next.mean[2]! + b * prev.mean[2]!,
  );
  const covariance = new Float64Array(9);
  for (let i = 0; i < 9; i++) covariance[i] = a * next.covariance[i]! + b * prev.covariance[i]!;
  return { mean, covariance, count: next.count };
}

/** Clamp a gain to the slider's range, treating nonsense as unity. */
function clampGain(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 1;
  return Math.max(GAIN_MIN, Math.min(GAIN_MAX, v));
}

/** `YYYYMMDD-HHMMSS`, for filenames. Local time, because a field session is. */
function stamp(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
  );
}

/** Save a blob under a filename, without touching the network. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = el('a', { href: url });
  a.download = filename;
  // Safari ignores a click on an anchor that is not in the document.
  document.body.append(a);
  a.click();
  a.remove();
  // The download reads the object URL after this call returns.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// ---------------------------------------------------------------------------
// The render worker, from the main thread's side
// ---------------------------------------------------------------------------

interface Job {
  resolve: (reply: DoneReply | UpdatedReply) => void;
  reject: (error: Error) => void;
}

/**
 * A thin request/reply wrapper over the render worker.
 *
 * Every request carries a token and every reply echoes it, so several captures
 * can be in flight in either direction without their replies being confused.
 * A worker-level failure rejects everything outstanding rather than leaving
 * promises that never settle.
 */
class Renderer {
  private readonly worker: Worker;
  private readonly jobs = new Map<number, Job>();
  private nextToken = 1;

  constructor(url: string) {
    this.worker = new Worker(url, { type: 'module' });
    this.worker.onmessage = (event: MessageEvent<WorkerReply>) => this.receive(event.data);
    this.worker.onerror = (event) => {
      this.failAll(new Error(event.message || 'the render worker stopped unexpectedly'));
    };
  }

  private receive(reply: WorkerReply): void {
    const job = this.jobs.get(reply.token);
    if (!job) return;
    this.jobs.delete(reply.token);
    if (reply.kind === 'error') job.reject(new Error(reply.message));
    else job.resolve(reply);
  }

  private failAll(error: Error): void {
    for (const job of this.jobs.values()) job.reject(error);
    this.jobs.clear();
  }

  private send(
    build: (token: number) => WorkerRequest,
    transfer: Transferable[] = [],
  ): Promise<DoneReply | UpdatedReply> {
    const token = this.nextToken++;
    return new Promise((resolve, reject) => {
      this.jobs.set(token, { resolve, reject });
      this.worker.postMessage(build(token), transfer);
    });
  }

  /** Render a fresh capture, and encode its original, render, and thumbnail. */
  process(bitmap: ImageBitmap, preset: string, gain: number): Promise<DoneReply> {
    return this.send(
      (token) => ({ kind: 'process', token, bitmap, preset, gain }),
      [bitmap],
    ) as Promise<DoneReply>;
  }

  /** Re-render a stored shot from its original, at a new preset or gain. */
  rerender(original: Blob, preset: string, gain: number): Promise<UpdatedReply> {
    return this.send((token) => ({ kind: 'rerender', token, original, preset, gain })) as Promise<UpdatedReply>;
  }
}

/**
 * The worker's URL, on the same path as this bundle.
 *
 * Built from `import.meta.url` by hand rather than with
 * `new URL('./shot-worker.js', import.meta.url)`: the bundler recognises that
 * form and tries to resolve `shot-worker.js` on disk, where only
 * `shot-worker.ts` exists. The worker is its own build entry, so its path
 * relative to this bundle is known — and it stays correct under a GitHub Pages
 * project path, which a root-absolute `/js/...` would not.
 */
function workerUrl(): string {
  const here = import.meta.url;
  return `${here.slice(0, here.lastIndexOf('/') + 1)}shot-worker.js`;
}

// ---------------------------------------------------------------------------
// The page shown when there is no camera to be had
// ---------------------------------------------------------------------------

/** The card shown when the browser will not give us a camera at all. */
function blockedCard(reason: string, detail: string): HTMLElement {
  return el('article', { class: 'card' }, [
    el('div', { class: 'card__body' }, [
      el('div', { class: 'card__title' }, [
        el('h2', { text: 'Camera unavailable' }),
        el('span', { class: 'tag tag--approx', text: reason }),
      ]),
      el('p', { class: 'note', text: detail }),
      el('p', {}, [
        el('a', { class: 'btn btn--primary', href: 'upload.html', text: 'Use a photo instead' }),
        ' ',
        el('a', { class: 'btn', href: 'gallery.html', text: 'See examples' }),
      ]),
      el('p', {
        class: 'note',
        text:
          'A photograph through the upload page runs the identical stretch at full resolution. Nothing about the result depends on the camera.',
      }),
    ]),
  ]);
}

// ---------------------------------------------------------------------------
// The app
// ---------------------------------------------------------------------------

function cameraApp(gl: GlRenderer, canvas: HTMLCanvasElement): HTMLElement {
  // The video is not hidden. `display: none` can stop a browser from decoding
  // frames, so it sits under the canvas and is covered by it.
  const video = el('video', { class: 'cam__video' });
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('muted', '');
  canvas.className = 'cam__gl';

  const status = el('p', { class: 'cam__status' });
  const errorLine = el('p', { class: 'cam__err err hidden' });

  // -- settings sheet, for the live feed -----------------------------------

  const liveControls = presetControls(chooseLivePreset, setLiveGain);

  const resolutionValue = el('span', { text: '—' });
  const nativeValue = el('span', { text: '' });
  const resolution = el('input', {
    type: 'range',
    attrs: { min: '0', max: '0', step: '1', value: '0', 'aria-label': 'Preview resolution' },
  });

  const readout = el('div', { class: 'stat-line stat-line--readout' });

  const settingsClose = el('button', {
    class: 'btn',
    type: 'button',
    text: 'Close',
    dataset: { action: 'settings-close' },
  });

  const sheet = el('section', { class: 'cam__sheet', attrs: { 'aria-label': 'Settings' } }, [
    el('div', { class: 'cam__sheet-head' }, [
      el('h2', { text: 'Settings' }),
      settingsClose,
    ]),
    liveControls.root,
    el('label', { class: 'field', text: 'Preview resolution' }, []),
    resolution,
    el('div', { class: 'stat-line stat-line--resolution' }, [resolutionValue, nativeValue]),
    readout,
    el('div', { class: 'cam__sheet-foot' }, [
      el('a', { class: 'btn', href: 'gallery.html', text: 'Intro' }),
      el('a', { class: 'btn', href: 'upload.html', text: 'Upload a photo' }),
    ]),
  ]);

  // -- adjust panel, for one stored shot -----------------------------------

  const adjustControls = presetControls(chooseAdjustPreset, setAdjustGain, 'Adjust gain');
  const preview = el('img', { class: 'cam__preview', alt: '' });
  const adjustNote = el('p', { class: 'note cam__adjust-note' });
  const adjustStatus = el('div', { class: 'stat-line' });
  const adjustClose = el('button', {
    class: 'btn',
    type: 'button',
    text: 'Close',
    dataset: { action: 'adjust-close' },
  });

  const adjustPanel = el('section', { class: 'cam__adjust', attrs: { 'aria-label': 'Adjust shot' } }, [
    el('div', { class: 'cam__sheet-head' }, [
      el('h2', { text: 'Adjust' }),
      adjustClose,
    ]),
    preview,
    adjustNote,
    adjustControls.root,
    adjustStatus,
  ]);

  // -- shots panel ---------------------------------------------------------

  const shotsList = el('div', { class: 'shots__list' });
  const downloadAllBtn = el('button', {
    class: 'btn btn--primary',
    type: 'button',
    text: 'Download all',
    dataset: { action: 'download-all' },
  });
  const shotsClose = el('button', {
    class: 'btn',
    type: 'button',
    text: 'Close',
    dataset: { action: 'shots-close' },
  });
  const panel = el('section', { class: 'cam__panel', attrs: { 'aria-label': 'Shots' } }, [
    el('div', { class: 'cam__sheet-head' }, [
      el('h2', { text: 'Shots' }),
      el('div', { class: 'row' }, [downloadAllBtn, shotsClose]),
    ]),
    shotsList,
  ]);

  // -- overlays ------------------------------------------------------------

  const cog = el('button', {
    class: 'cam__cog',
    type: 'button',
    dataset: { action: 'settings' },
    attrs: { 'aria-label': 'Settings', 'aria-expanded': 'false' },
    html:
      '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M19.14 12.94a7.5 7.5 0 0 0 .06-.94 7.5 7.5 0 0 0-.06-.94l2.03-1.58a.48.48 0 0 0 .12-.61l-1.92-3.32a.48.48 0 0 0-.59-.22l-2.39.96a7.3 7.3 0 0 0-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.48.41l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.48.48 0 0 0-.59.22L2.65 8.87a.48.48 0 0 0 .12.61l2.03 1.58c-.04.31-.06.62-.06.94s.02.63.06.94l-2.03 1.58a.48.48 0 0 0-.12.61l1.92 3.32c.12.22.37.3.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.04.24.24.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54a7.3 7.3 0 0 0 1.62-.94l2.39.96c.22.08.48 0 .59-.22l1.92-3.32a.48.48 0 0 0-.12-.61l-2.03-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/>' +
      '</svg>',
  });

  const lastThumb = el('img', { class: 'cam__shots-thumb', alt: '' });
  const shotsBtn = el('button', {
    class: 'cam__shots',
    type: 'button',
    text: 'No shots yet',
    dataset: { action: 'shots' },
    attrs: { 'aria-label': 'Shots', 'aria-expanded': 'false' },
  });

  const shutter = el('button', {
    class: 'cam__shutter',
    type: 'button',
    dataset: { action: 'shutter' },
    attrs: { 'aria-label': 'Take a shot' },
  });

  const stage = el('div', { class: 'cam__stage' }, [
    video,
    canvas,
    el('div', { class: 'cam__hud' }, [status, errorLine]),
    cog,
    shotsBtn,
    el('div', { class: 'cam__bar' }, [shutter]),
    sheet,
    panel,
    adjustPanel,
  ]);

  // -- live state ----------------------------------------------------------

  /** The live preset and gain; the shot being edited keeps its own. */
  let livePreset = DEFAULT_PRESET;
  let liveGain = 1;
  /** The shot the adjust panel is editing, or `null` when it is closed. */
  let adjusting: { id: number; preset: string; gain: number } | null = null;

  let smoothed: Stats | null = null;
  let affine: GlAffine | null = null;
  let useLut = false;
  let lastStatsAt = 0;
  let nativeWidth = 0;
  let nativeHeight = 0;
  let widths: number[] = [];
  let liveWidth = 0;
  let liveHeight = 0;
  let lastFrameMs = 0;
  let lastReadoutAt = 0;
  let fps = 0;
  let lastFrameAt = 0;
  let rerenderTimer = 0;

  let shots: Shot[] = [];
  /** Object URLs owned by the shots panel's strip; revoked on every repaint. */
  let thumbUrls: string[] = [];
  /** The object URL the corner preview is showing, owned separately. */
  let lastThumbUrl: string | null = null;
  /** The object URL the adjust preview is showing, owned separately. */
  let previewUrl: string | null = null;

  // -- the live stretch ----------------------------------------------------

  function rebuildLive(): void {
    const meta = metaFor(livePreset);
    if (meta.needs === 'none') {
      // Nonlinear: no affine can express it, so the frame loop uses the LUT.
      affine = null;
      useLut = true;
      return;
    }
    useLut = false;
    if (!smoothed) {
      affine = null;
      return;
    }
    const params = presetById(livePreset).build(smoothed);
    // The gain slider scales the band gain. `M = gain * R D' E^T`, so this is a
    // pure contrast control, and the same one the collapse already models.
    params.gain = (params.gain ?? 1) * liveGain;
    affine = quantise(collapse(params));
  }

  /** Build the slider stops from the stream's own size, and pick a default. */
  function computeResolutions(): void {
    if (!nativeWidth || !nativeHeight) return;
    const min = Math.min(MIN_LIVE_WIDTH, nativeWidth);
    const seen = new Set<number>();
    const picks: number[] = [];
    for (const scale of LIVE_SCALES) {
      // A multiple of two keeps the height integral on common aspect ratios.
      const w = Math.max(min, Math.min(nativeWidth, Math.round((nativeWidth * scale) / 2) * 2));
      if (!seen.has(w)) {
        seen.add(w);
        picks.push(w);
      }
    }
    if (picks[picks.length - 1] !== nativeWidth) picks.push(nativeWidth);
    picks.sort((a, b) => a - b);
    widths = picks;

    // Default to the largest stop at or below `DEFAULT_LIVE_WIDTH`, so a 4K
    // phone starts smooth and a small webcam starts at native.
    let index = 0;
    for (let i = 0; i < widths.length; i++) if (widths[i]! <= DEFAULT_LIVE_WIDTH) index = i;

    resolution.max = String(Math.max(0, widths.length - 1));
    resolution.value = String(index);
    applyResolution(index);
  }

  /** Apply slider stop `index`: resize the drawing buffer, keep the aspect. */
  function applyResolution(index: number): void {
    if (!nativeWidth) return;
    const clamped = Math.max(0, Math.min(widths.length - 1, index));
    const w = widths[clamped] ?? nativeWidth;
    liveWidth = Math.max(1, Math.min(nativeWidth, w));
    liveHeight = Math.max(1, Math.round((liveWidth * nativeHeight) / nativeWidth));
    // Contain, not cover: cropping a rock-art panel to fill the screen would
    // hide the edge the pigment is on.
    resolutionValue.textContent = `${liveWidth}×${liveHeight}`;
    nativeValue.textContent = `native ${nativeWidth}×${nativeHeight}`;
  }

  function measureStats(now: number): void {
    if (smoothed && now - lastStatsAt < STATS_INTERVAL_MS) return;
    const small = rasterFromElement(video, STATS_W, STATS_H);
    const next = rasterStats(small, 4000);
    // After a preset change `smoothed` is null, so the first measurement of the
    // new preset is used as given rather than blended with the old one's.
    smoothed = smoothed ? blend(smoothed, next) : next;
    lastStatsAt = now;
    rebuildLive();
  }

  function updateReadout(): void {
    const now = performance.now();
    if (now - lastReadoutAt < READOUT_INTERVAL_MS) return;
    lastReadoutAt = now;
    const meta = metaFor(livePreset);
    readout.replaceChildren(
      el('span', { text: `${liveWidth}x${liveHeight}` }),
      el('span', { text: `${fps.toFixed(0)} fps` }),
      el('span', { text: `frame ${lastFrameMs.toFixed(1)} ms` }),
      el('span', { text: `gain x${liveGain.toFixed(2)}` }),
      el('span', { text: meta.needs === 'none' ? '3D LUT' : meta.fixed ? 'fixed M' : 'adaptive M' }),
      smoothed
        ? el('span', { text: `mean ${Array.from(smoothed.mean).map((v) => v.toFixed(0)).join(',')}` })
        : el('span', { text: 'mean —' }),
      el('span', { text: String(shots.length) + ' shots' }),
      el('span', { text: rendererName(gl.gl) }),
    );
  }

  function frame(now: number): void {
    if (video.readyState >= 2 && video.videoWidth > 0) {
      const meta = metaFor(livePreset);
      if (meta.needs !== 'none') measureStats(now);
      if (liveWidth && (useLut || affine)) {
        const t0 = performance.now();
        const options = {
          source: video,
          sourceWidth: video.videoWidth,
          sourceHeight: video.videoHeight,
          width: liveWidth,
          height: liveHeight,
        };
        if (useLut) gl.drawElementLut(labLutCached(), options);
        else if (affine) gl.drawElement(affine, options);
        lastFrameMs = performance.now() - t0;
        if (lastFrameAt > 0) {
          const dt = now - lastFrameAt;
          if (dt > 0) fps = fps === 0 ? 1000 / dt : fps * 0.9 + (1000 / dt) * 0.1;
        }
        lastFrameAt = now;
      }
    }
    updateReadout();
  }

  // -- controls ------------------------------------------------------------

  /** Make the live chips, the gain slider and the blurb tell the same story. */
  function syncLiveControls(): void {
    liveControls.sync(livePreset, liveGain);
  }

  /** Make the adjust panel's copy tell the same story about the shot. */
  function syncAdjustControls(): void {
    if (!adjusting) return;
    adjustControls.sync(adjusting.preset, adjusting.gain);
    const shot = shots.find((s) => s.id === adjusting!.id);
    adjustNote.textContent = shot
      ? `Adjusting the shot from ${new Date(shot.createdAt).toLocaleString()}. Changes are saved.`
      : '';
  }

  function chooseLivePreset(id: string): void {
    if (id === livePreset) return;
    livePreset = id;
    // Force a fresh measurement: the new preset's matrix may depend on
    // statistics collected under the old one.
    smoothed = null;
    lastStatsAt = 0;
    affine = null;
    useLut = false;
    rebuildLive();
    syncLiveControls();
  }

  function chooseAdjustPreset(id: string): void {
    if (!adjusting) return;
    adjusting.preset = id;
    syncAdjustControls();
    scheduleShotRender();
  }

  function setLiveGain(v: number): void {
    liveGain = v;
    syncLiveControls();
    rebuildLive();
  }

  function setAdjustGain(v: number): void {
    if (!adjusting) return;
    adjusting.gain = v;
    syncAdjustControls();
    scheduleShotRender();
  }

  resolution.addEventListener('input', () => {
    applyResolution(Number(resolution.value));
  });

  // -- sheets --------------------------------------------------------------

  let settingsOpen = false;
  let shotsOpen = false;
  let adjustOpen = false;

  function showSettings(open: boolean): void {
    settingsOpen = open;
    sheet.classList.toggle('is-open', open);
    cog.setAttribute('aria-expanded', String(open));
    if (open) {
      if (shotsOpen) showShots(false);
      if (adjustOpen) showAdjust(false);
      syncLiveControls();
    }
  }

  function showShots(open: boolean): void {
    shotsOpen = open;
    panel.classList.toggle('is-open', open);
    shotsBtn.setAttribute('aria-expanded', String(open));
    if (open) {
      if (settingsOpen) showSettings(false);
      if (adjustOpen) showAdjust(false);
      void refreshShots();
    }
  }

  /** Open or close the shot editor. Closing it ends the adjustment. */
  function showAdjust(open: boolean): void {
    adjustOpen = open;
    adjustPanel.classList.toggle('is-open', open);
    if (open) {
      if (settingsOpen) showSettings(false);
      if (shotsOpen) showShots(false);
    } else {
      adjusting = null;
      clearPreview();
    }
  }

  function showError(message: string): void {
    errorLine.textContent = message;
    errorLine.classList.remove('hidden');
  }

  function clearError(): void {
    errorLine.textContent = '';
    errorLine.classList.add('hidden');
  }

  cog.addEventListener('click', () => showSettings(!settingsOpen));
  shotsBtn.addEventListener('click', () => showShots(!shotsOpen));
  settingsClose.addEventListener('click', () => showSettings(false));
  shotsClose.addEventListener('click', () => showShots(false));
  adjustClose.addEventListener('click', () => showAdjust(false));

  // -- shots ---------------------------------------------------------------

  /** Revoke the object URLs the previous strip made. */
  function releaseThumbs(): void {
    for (const url of thumbUrls) URL.revokeObjectURL(url);
    thumbUrls = [];
  }

  function setLastThumb(shot: Shot | undefined): void {
    // The corner preview keeps its own URL. Sharing the strip's list would let
    // `releaseThumbs` revoke the image the corner is still showing.
    if (lastThumbUrl) URL.revokeObjectURL(lastThumbUrl);
    lastThumbUrl = null;
    if (!shot) {
      lastThumb.removeAttribute('src');
      lastThumb.style.removeProperty('aspect-ratio');
      shotsBtn.textContent = 'No shots yet';
      shotsBtn.classList.add('is-empty');
      return;
    }
    const url = URL.createObjectURL(shot.thumb);
    lastThumbUrl = url;
    lastThumb.src = url;
    lastThumb.alt = `Last shot: ${shot.width}×${shot.height}`;
    lastThumb.style.setProperty('aspect-ratio', `${shot.width} / ${shot.height}`);
    shotsBtn.replaceChildren(lastThumb, el('span', { class: 'cam__shots-count', text: String(shots.length) }));
    shotsBtn.classList.remove('is-empty');
  }

  /** The preset name for a shot, falling back to the raw id. */
  function presetLabel(id: string): string {
    return SUMMARIES.find((s) => s.id === id)?.name ?? id;
  }

  /**
   * One row of the shots panel.
   *
   * Delete asks once. The store may hold the only copy of a frame, and there is
   * no undo, so a single tap must not be enough.
   */
  function shotTile(shot: Shot): HTMLElement {
    const thumb = el('img', { class: 'shot__thumb', alt: `Shot ${shot.width}×${shot.height}` });
    const url = URL.createObjectURL(shot.thumb);
    thumbUrls.push(url);
    thumb.src = url;
    // Full tile width at the sensor's ratio: a square crop hides the edges
    // where the faint pigment usually is.
    thumb.style.setProperty('aspect-ratio', `${shot.width} / ${shot.height}`);

    const when = el('span', { text: new Date(shot.createdAt).toLocaleTimeString() });
    const size = el('span', { text: `${shot.width}×${shot.height}` });
    const preset = el('span', { text: presetLabel(shot.preset) });
    const gainText = el('span', { text: gainIgnored(shot.preset) ? '—' : `x${shot.gain.toFixed(2)}` });

    let armed = false;
    const remove = el('button', {
      class: 'btn shot__danger',
      type: 'button',
      text: 'Delete',
      dataset: { action: 'delete', shot: String(shot.id) },
    });
    remove.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        remove.textContent = 'Confirm delete';
        remove.classList.add('is-armed');
        return;
      }
      void removeShot(shot);
    });

    return el('article', { class: 'shot', dataset: { shot: String(shot.id) } }, [
      thumb,
      el('div', { class: 'shot__meta' }, [
        el('div', { class: 'shot__facts' }, [size, preset, gainText, when]),
      ]),
      el('div', { class: 'shot__actions' }, [
        el('button', {
          class: 'btn',
          type: 'button',
          text: 'Adjust',
          dataset: { action: 'adjust', shot: String(shot.id) },
          on: { click: () => startEditing(shot) },
        }),
        el('button', {
          class: 'btn',
          type: 'button',
          text: 'PNG',
          dataset: { action: 'download', shot: String(shot.id) },
          on: {
            click: () => saveBlob(shot.rendered, `${APP_SLUG}-${stamp(shot.createdAt)}-${shot.preset}.png`),
          },
        }),
        el('button', {
          class: 'btn',
          type: 'button',
          text: 'Original',
          dataset: { action: 'original', shot: String(shot.id) },
          on: {
            click: () =>
              saveBlob(shot.original, `${APP_SLUG}-${stamp(shot.createdAt)}-original.png`),
          },
        }),
        remove,
      ]),
    ]);
  }

  function renderShots(): void {
    releaseThumbs();
    if (shots.length === 0) {
      shotsList.replaceChildren(
        el('p', {
          class: 'empty',
          text: 'Nothing captured yet. The shutter stores each frame here, with its original, so you can change preset or gain later.',
        }),
      );
      downloadAllBtn.disabled = true;
      return;
    }
    downloadAllBtn.disabled = false;
    shotsList.replaceChildren(...shots.map(shotTile));
  }

  /** Reload from the store and repaint the strip and the corner preview. */
  async function refreshShots(): Promise<void> {
    try {
      shots = await listShots();
    } catch (err) {
      showError(`Could not read the shot store: ${err instanceof Error ? err.message : String(err)}`);
      shots = [];
    }
    renderShots();
    setLastThumb(shots[0]);
  }

  /** Drop the adjust preview's object URL and clear the image. */
  function clearPreview(): void {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = null;
    preview.removeAttribute('src');
  }

  /**
   * Show `blob` in the adjust preview at the shot's own aspect ratio.
   *
   * The image spans the panel's full width, and its height follows from the
   * sensor's ratio — a square crop here would hide the frame the stretch is for.
   */
  function setPreview(blob: Blob, shot: { width: number; height: number }): void {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = URL.createObjectURL(blob);
    preview.src = previewUrl;
    preview.alt = `Stretched preview, ${shot.width}×${shot.height}`;
    preview.style.setProperty('aspect-ratio', `${shot.width} / ${shot.height}`);
  }

  function startEditing(shot: Shot): void {
    adjusting = { id: shot.id!, preset: shot.preset, gain: shot.gain };
    syncAdjustControls();
    setPreview(shot.rendered, shot);
    adjustStatus.replaceChildren();
    showShots(false);
    showSettings(false);
    showAdjust(true);
  }

  async function removeShot(shot: Shot): Promise<void> {
    try {
      await deleteShot(shot.id!);
    } catch (err) {
      showError(`Could not delete the shot: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    if (adjusting?.id === shot.id) showAdjust(false);
    await refreshShots();
    syncAdjustControls();
  }

  /**
   * Re-render the shot under adjustment, after the changes stop arriving.
   *
   * Dragging the gain slider fires an event per pixel of travel, and each one
   * would start a full-resolution render. The debounce is the difference
   * between one render and thirty.
   */
  function scheduleShotRender(): void {
    if (!adjusting) return;
    window.clearTimeout(rerenderTimer);
    rerenderTimer = window.setTimeout(() => void renderShot(), RERENDER_DEBOUNCE_MS);
  }

  async function renderShot(): Promise<void> {
    if (!adjusting) return;
    const id = adjusting.id;
    const preset = adjusting.preset;
    const gain = gainIgnored(preset) ? 1 : adjusting.gain;
    const shot = shots.find((s) => s.id === id);
    if (!shot || !renderer) return;
    adjustStatus.replaceChildren(el('span', { text: `Stretching ${presetLabel(preset)}…` }));
    try {
      const reply = await renderer.rerender(shot.original, preset, gain);
      // The user may have moved on while this ran; only the newest edit wins.
      if (!adjusting || adjusting.id !== id) return;
      const updated: Shot = { ...shot, preset, gain, rendered: reply.rendered, thumb: reply.thumb };
      await putShot(updated);
      shots = shots.map((s) => (s.id === id ? updated : s));
      if (shotsOpen) renderShots();
      setLastThumb(shots[0]);
      setPreview(reply.rendered, updated);
      adjustStatus.replaceChildren(el('span', { text: `Re-rendered in ${reply.ms.toFixed(0)} ms.` }));
      clearError();
    } catch (err) {
      showError(`Could not re-render the shot: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // -- capture -------------------------------------------------------------

  async function takeShot(): Promise<void> {
    if (!renderer) {
      showError('The render worker is not available, so a shot cannot be saved.');
      return;
    }
    if (video.readyState < 2 || !video.videoWidth) {
      showError('No frame to capture yet.');
      return;
    }
    clearError();
    shutter.classList.add('is-busy');
    // A white flash is the only acknowledgement a still camera needs.
    stage.classList.add('is-flash');
    window.setTimeout(() => stage.classList.remove('is-flash'), 120);
    try {
      // At the camera's own resolution, not the preview's: a still is not
      // time-sensitive, and this is the frame the shot store keeps.
      const bitmap = await createImageBitmap(video);
      const preset = livePreset;
      const gain = gainIgnored(preset) ? 1 : liveGain;
      const reply = await renderer.process(bitmap, preset, gain);
      const stored = await addShot({
        createdAt: Date.now(),
        width: reply.width,
        height: reply.height,
        preset,
        gain,
        original: reply.original,
        rendered: reply.rendered,
        thumb: reply.thumb,
      });
      shots = [stored, ...shots];
      if (shotsOpen) renderShots();
      setLastThumb(stored);
      status.textContent = `Saved ${reply.width}×${reply.height} in ${reply.ms.toFixed(0)} ms.`;
    } catch (err) {
      showError(`Could not save the shot: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      shutter.classList.remove('is-busy');
    }
  }

  shutter.addEventListener('click', () => void takeShot());

  // -- bulk download -------------------------------------------------------

  downloadAllBtn.addEventListener('click', () => {
    void (async () => {
      if (shots.length === 0) return;
      downloadAllBtn.disabled = true;
      const was = downloadAllBtn.textContent;
      downloadAllBtn.textContent = `Packing ${shots.length}…`;
      try {
        // Newest first in the panel means oldest first in the archive's names,
        // so a sorted file listing reads in capture order.
        const ordered = [...shots].sort((a, b) => a.createdAt - b.createdAt);
        const blob = await zipBlob(
          ordered.map((shot) => ({
            name: `${stamp(shot.createdAt)}-${shot.preset}-${shot.width}x${shot.height}.png`,
            data: shot.rendered,
          })),
        );
        saveBlob(blob, `${APP_SLUG}-shots-${stamp(Date.now())}.zip`);
        status.textContent = `Packed ${ordered.length} shot(s), ${(blob.size / 1e6).toFixed(1)} MB.`;
        clearError();
      } catch (err) {
        showError(`Could not build the archive: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        downloadAllBtn.textContent = was;
        downloadAllBtn.disabled = shots.length === 0;
      }
    })();
  });

  // -- stream --------------------------------------------------------------

  async function requestStream(): Promise<MediaStream> {
    const wanted = {
      facingMode: { ideal: 'environment' },
      width: { ideal: 3840 },
      height: { ideal: 2160 },
    };
    try {
      return await navigator.mediaDevices.getUserMedia({ video: wanted, audio: false });
    } catch {
      // A camera that refuses the high-resolution request still gets a live
      // feed; the slider then spans whatever it does support.
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
    }
  }

  /**
   * The per-frame callback chain, and the token that owns it.
   *
   * `requestVideoFrameCallback` fires once per decoded frame, so a callback
   * scheduled while the stream is stopped never fires. Android stops the stream
   * when the page leaves the foreground; the old callback then waits forever,
   * and the canvas keeps the last frame. A fresh start bumps the token, and the
   * stale chain stops itself on the next fire.
   */
  let loopToken = 0;

  function startFrameLoop(): void {
    const token = ++loopToken;
    const step = (): void => {
      if (token !== loopToken) return;
      frame(performance.now());
      nextFrame(video, step);
    };
    nextFrame(video, step);
  }

  /** Stop the current stream's tracks, if any, so the camera is released. */
  function stopStream(): void {
    const stream = video.srcObject as MediaStream | null;
    if (!stream) return;
    for (const track of stream.getTracks()) track.stop();
    video.srcObject = null;
  }

  let starting = false;

  async function start(): Promise<void> {
    if (starting) return;
    starting = true;
    status.textContent = 'Requesting camera…';
    try {
      stopStream();
      const stream = await requestStream();
      video.srcObject = stream;
      await video.play();
      if (!video.videoWidth) {
        await new Promise<void>((resolve) => {
          video.addEventListener('loadedmetadata', () => resolve(), { once: true });
        });
      }
      nativeWidth = video.videoWidth;
      nativeHeight = video.videoHeight;
      computeResolutions();
      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings();
      status.textContent = settings
        ? `Camera running: ${nativeWidth}x${nativeHeight}, ${settings.frameRate?.toFixed(0) ?? '?'} fps.`
        : `Camera running: ${nativeWidth}x${nativeHeight}.`;
      clearError();
      startFrameLoop();
      // Build the LAB table while the page is idle, so the first tap of the LAB
      // chip does not wait for it.
      window.setTimeout(() => {
        try {
          labLutCached();
        } catch {
          // A LUT that cannot be built will raise again, visibly, on selection.
        }
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      status.textContent = '';
      showError(`Could not start the camera: ${message}`);
    } finally {
      starting = false;
    }
  }

  /**
   * Resume after the page comes back to the foreground.
   *
   * A surviving track only needs `play()` again and a fresh frame chain. A
   * track the OS ended or muted needs the whole stream requested afresh.
   */
  async function resumeStream(): Promise<void> {
    if (document.visibilityState !== 'visible') return;
    const stream = video.srcObject as MediaStream | null;
    const track = stream?.getVideoTracks()[0];
    if (!stream || !track || track.readyState === 'ended' || track.muted) {
      await start();
      return;
    }
    try {
      if (video.paused) await video.play();
    } catch (err) {
      showError(`Could not resume the camera: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    startFrameLoop();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void resumeStream();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) void resumeStream();
  });

  let renderer: Renderer | null = null;
  try {
    renderer = new Renderer(workerUrl());
  } catch (err) {
    showError(`The render worker could not start: ${err instanceof Error ? err.message : String(err)}`);
  }

  syncLiveControls();
  void start();
  void refreshShots();

  return stage;
}

/** The nav is hidden while the camera runs: the viewport is the camera. */
async function main(): Promise<void> {
  titleFor('Camera');
  const root: HTMLElement = (() => {
    const found = document.getElementById('app');
    if (!found) throw new Error('#app missing');
    return found;
  })();

  const blocked = (reason: string, detail: string): void => {
    document.body.classList.remove('is-camera');
    root.replaceChildren(
      el('div', {}, [
        header('camera'),
        el('main', { class: 'wrap' }, [blockedCard(reason, detail)]),
      ]),
    );
  };

  if (!window.isSecureContext) {
    blocked(
      'insecure context',
      'This page is served over plain HTTP, so the browser will not hand out a camera. Use HTTPS (or localhost), or send a photograph instead.',
    );
    return;
  }
  if (typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    blocked('no media API', 'This browser exposes no getUserMedia, so a live feed is not possible here.');
    return;
  }
  if (typeof Worker !== 'function') {
    blocked('no web workers', 'This browser cannot run the render worker that a saved shot needs.');
    return;
  }

  const canvas = el('canvas');
  let gl: GlRenderer;
  try {
    gl = new GlRenderer(canvas);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    blocked('no WebGL2', `The live path needs a WebGL2 context to keep up. The browser said: ${message}`);
    return;
  }

  document.body.classList.add('is-camera');
  root.replaceChildren(cameraApp(gl, canvas));
}

void main();
